import { useAdminCapabilities } from "hooks/useAdminCapabilities";
import { AdminCapability } from "shared/utils/capabilities";
import { useQuery } from "@tanstack/react-query";
import client from "shared/utils/axios-config";
import { CustomerTable, UserParams } from "shared/types";
import { message } from "antd";
import { translate } from "shared/languages";

/* eslint-disable react-hooks/rules-of-hooks */
const getAllCustomerByBusinessId = (userParams?: UserParams) => {
  return useQuery({
    queryKey: ["customer", userParams],
    queryFn: async (): Promise<CustomerTable[] | undefined> => {
      const res = await client.get(`/v1/customer`, { params: userParams });
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      } else {
        message.error(translate("messages.error.retrieve_data"));
      }
    },
    staleTime: 5 * 60_000,
  });
};

const getAvailableCustomerByScheduleId = (schedule_id: string) => {
  return useQuery({
    queryKey: ["customer", "schedule", schedule_id],
    queryFn: async (): Promise<CustomerTable[] | undefined> => {
      const res = await client.get(`/v1/customer/schedule/${schedule_id}`);
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      } else {
        message.error(translate("messages.error.retrieve_data"));
      }
    },
  });
};

// The customer's Spott login credentials an admin can re-share via WhatsApp:
// the 6-char login code (generated once at registration; null if not yet
// backfilled) and the magic login link (the server lazily creates a missing
// link row on this read, so link is always set on a current server; an older
// server omits the field and the UI hides the row).
export interface LoginCredentials {
  code: string | null;
  link: string | null;
}

// Skipped entirely when a Manager has restricted this surface's
// credentials: the server answers 403, and firing a request that can only
// fail would put a red line in the console on every profile view.
const useGetCustomerLoginCode = (customerId: string) => {
  const { isRestricted } = useAdminCapabilities();
  const restricted = isRestricted(AdminCapability.LoginCredentialsClient);
  return useQuery({
    queryKey: ["customer", customerId, "login-code"],
    queryFn: async (): Promise<LoginCredentials> => {
      const res = await client.get(`/v1/customer/${customerId}/login-code`);
      if (res.status === 200 && !res.data?.error) {
        return {
          code: res.data.data?.code ?? null,
          link: res.data.data?.link ?? null,
        };
      }
      return { code: null, link: null };
    },
    enabled: !!customerId && !restricted,
    staleTime: 5 * 60_000,
  });
};

export {
  getAllCustomerByBusinessId,
  getAvailableCustomerByScheduleId,
  useGetCustomerLoginCode,
};
